import { Fragment, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { useConfirm } from '../../context/ConfirmContext';
import {
  useMatchingBewerber,
  useMarkBewerberNichtGeeignet,
  useUnmarkBewerberNichtGeeignet,
  useFahrzeit,
} from '../../api/bedarfe';
import { CreateKennenlernenModal } from './CreateKennenlernenModal';
import { BedarfSendenModal } from './BedarfSendenModal';
import {
  BewerberStatus,
  BewerberStatusConfig,
  MAX_ROUTE_DISTANCE_KM,
  formatWochenstundenWunsch,
} from '../../types/bewerber';
import {
  MatchTypeConfig,
  GeoPrecisionConfig,
  DEFAULT_RADIUS_KM,
  MATCHING_PAGE_SIZE,
  RADIUS_OPTIONS,
  type MatchingBewerber,
  type MatchingMode,
} from '../../types/kennenlernen';
import { useEinladenBewerber } from '../bewerber/actions/useEinladenBewerber';
import { EinladenModal } from '../bewerber/EinladenModal';

interface MatchingBewerberSectionProps {
  bedarfId: string;
  childName: string;
  schoolCity: string | null;
  schoolName?: string | null;
}

export function MatchingBewerberSection({
  bedarfId,
  childName,
  schoolCity,
  schoolName,
}: MatchingBewerberSectionProps) {
  const confirm = useConfirm();

  const [mode, setMode] = useState<MatchingMode>('CITY');
  const [radiusKm, setRadiusKm] = useState<number>(DEFAULT_RADIUS_KM);
  const [limit, setLimit] = useState<number>(MATCHING_PAGE_SIZE);
  /** Id of the row whose travel time is currently being calculated. */
  const [routingId, setRoutingId] = useState<string | null>(null);

  const { data, isLoading, error } = useMatchingBewerber(bedarfId, true, {
    mode,
    radiusKm,
    limit,
  });
  const einladen = useEinladenBewerber();
  const markNichtGeeignet = useMarkBewerberNichtGeeignet();
  const unmarkNichtGeeignet = useUnmarkBewerberNichtGeeignet();
  const fahrzeit = useFahrzeit();

  const changeMode = (next: MatchingMode) => {
    setMode(next);
    setLimit(MATCHING_PAGE_SIZE);
  };

  const changeRadius = (next: number) => {
    setRadiusKm(next);
    setLimit(MATCHING_PAGE_SIZE);
  };

  const handleFahrzeit = (bewerber: MatchingBewerber) => {
    setRoutingId(bewerber.id);
    fahrzeit.mutate(
      { bewerberId: bewerber.id, bedarfId },
      {
        onError: (err: any) => {
          const message =
            err?.response?.data?.message || 'Fahrzeit konnte nicht berechnet werden';
          toast.error(Array.isArray(message) ? message.join(', ') : message);
        },
        onSettled: () => setRoutingId(null),
      },
    );
  };

  const [modalData, setModalData] = useState<{
    isOpen: boolean;
    bewerberId: string;
    bewerberName: string;
  } | null>(null);

  const [sendenBewerber, setSendenBewerber] = useState<MatchingBewerber | null>(null);

  // Statuses for which the Bedarf may be presented to the Bewerber. OFFBOARDING
  // is among them: someone whose position is ending may take the next one.
  const bedarfSendenStatuses: string[] = [
    BewerberStatus.GEEIGNET,
    BewerberStatus.EINGELADEN,
    BewerberStatus.VORSTELLUNG,
    BewerberStatus.IM_POOL,
    BewerberStatus.KENNENLERNGESPRAECH,
    BewerberStatus.OFFBOARDING,
  ];

  const handleInvite = async (bewerber: MatchingBewerber) => {
    const confirmed = await confirm({
      title: 'Bewerber einladen',
      message: `Möchten Sie ${bewerber.firstName} ${bewerber.lastName} einladen? Es wird automatisch eine Einladungs-E-Mail verschickt.`,
      confirmLabel: 'Einladen',
      variant: 'info',
    });
    if (!confirmed) return;

    // The shared invite hook sends the invitation email first and only changes
    // the status if the email succeeds. If the bewerber already has invitation
    // emails (rare for matching list), the EinladenModal opens to confirm a
    // repeat invitation.
    einladen.invite(bewerber);
  };

  const handlePlanKennenlernen = (bewerber: MatchingBewerber) => {
    setModalData({
      isOpen: true,
      bewerberId: bewerber.id,
      bewerberName: `${bewerber.firstName} ${bewerber.lastName}`,
    });
  };

  const closeModal = () => {
    setModalData(null);
  };

  const handleToggleNichtGeeignet = async (bewerber: MatchingBewerber) => {
    const name = `${bewerber.firstName} ${bewerber.lastName}`;
    try {
      if (bewerber.nichtGeeignet) {
        await unmarkNichtGeeignet.mutateAsync({ bedarfId, bewerberId: bewerber.id });
        toast.success(`${name} wieder als geeignet markiert`);
      } else {
        await markNichtGeeignet.mutateAsync({ bedarfId, bewerberId: bewerber.id });
        toast.success(`${name} für diesen Bedarf als nicht geeignet markiert`);
      }
    } catch (err: any) {
      const message = err?.response?.data?.message || 'Aktion fehlgeschlagen';
      toast.error(Array.isArray(message) ? message.join(', ') : message);
    }
  };

  const isTogglingNichtGeeignet =
    markNichtGeeignet.isPending || unmarkNichtGeeignet.isPending;

  const matches = data?.data || [];
  const meta = data?.meta;
  const total = meta?.total ?? matches.length;
  const isRadius = mode === 'RADIUS';

  const subtitle = isRadius
    ? `Bewerber im Umkreis von ${radiusKm} km um ${schoolName || schoolCity || 'die Schule'}`
    : schoolCity
      ? `Bewerber für ${schoolCity}`
      : 'Keine Stadt für die Schule angegeben';

  /**
   * Shared card shell. The mode switch lives in the header of every state —
   * including the empty and error ones — so a Bedarf without a school city can
   * still be switched to Umkreis, where only the address matters.
   */
  const renderCard = (body: React.ReactNode) => (
    <div className="bg-white shadow rounded-lg">
      <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h3 className="text-lg leading-6 font-medium text-gray-900">Passende Bewerber</h3>
            <p className="mt-1 text-sm text-gray-500">{subtitle}</p>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <div className="inline-flex rounded-lg border border-gray-300 bg-white p-1">
              <button
                type="button"
                onClick={() => changeMode('CITY')}
                className={`inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                  mode === 'CITY'
                    ? 'bg-primary-100 text-primary-700'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                Stadt
              </button>
              <button
                type="button"
                onClick={() => changeMode('RADIUS')}
                className={`inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                  isRadius
                    ? 'bg-primary-100 text-primary-700'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                Umkreis
              </button>
            </div>
            {isRadius && (
              <select
                value={radiusKm}
                onChange={(e) => changeRadius(Number(e.target.value))}
                aria-label="Radius"
                className="rounded-md border border-gray-300 py-1.5 pl-2 pr-7 text-sm text-gray-700 focus:border-primary-500 focus:ring-primary-500"
              >
                {RADIUS_OPTIONS.map((option) => (
                  <option key={option} value={option}>
                    {option} km
                  </option>
                ))}
              </select>
            )}
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-800">
              {total} gefunden
            </span>
          </div>
        </div>
        {isRadius && meta && meta.schoolGeocoded && meta.schoolPrecision !== 'EXACT' && (
          <p className="mt-2 text-xs text-amber-700">
            Schuladresse nur auf {GeoPrecisionConfig[meta.schoolPrecision].label} verortet —
            die Entfernungen sind entsprechend ungenau.
          </p>
        )}
      </div>
      <div className="px-4 py-5 sm:p-6">{body}</div>
    </div>
  );

  // A missing school city used to end the card here. It no longer can: someone
  // who applied to this Bedarf belongs in the list whether or not the school has
  // a city, so the note moved above the list instead of replacing it.
  const missingSchoolCity = !schoolCity && !isRadius;

  if (isLoading) {
    return renderCard(
      <div className="text-center">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600 mx-auto"></div>
        <p className="mt-2 text-sm text-gray-500">Laden...</p>
      </div>,
    );
  }

  if (error) {
    return renderCard(<p className="text-sm text-red-600">Fehler beim Laden der Bewerber</p>);
  }

  if (isRadius && meta && !meta.schoolGeocoded) {
    return renderCard(
      <p className="text-sm text-gray-500">
        Die Schuladresse wurde noch nicht verortet. Sie wird automatisch im Hintergrund
        nachgetragen — bitte in Kürze erneut versuchen.
      </p>,
    );
  }

  return (
    <>
      {renderCard(
        <>
          {missingSchoolCity && (
            <p className="mb-4 text-sm text-gray-500">
              Keine Stadt für die Schule angegeben. Bitte geben Sie eine Stadt an oder wechseln
              Sie in den Umkreis-Modus, der die Schuladresse verwendet.
            </p>
          )}
          {matches.length === 0 ? (
            <p className="text-sm text-gray-500">
              {isRadius
                ? `Keine passenden Bewerber im Umkreis von ${radiusKm} km gefunden.`
                : schoolCity
                  ? `Keine passenden Bewerber für ${schoolCity} gefunden.`
                  : 'Keine passenden Bewerber gefunden.'}
            </p>
          ) : (
            <ul className="divide-y divide-gray-200">
              {matches.map((bewerber) => {
                const statusConfig = BewerberStatusConfig[bewerber.status as BewerberStatus];
                const matchConfig = MatchTypeConfig[bewerber.matchType];
                const precisionConfig = GeoPrecisionConfig[bewerber.geoPrecision];
                const isNichtGeeignet = bewerber.nichtGeeignet;
                // A left accent bar rather than a full ring: two applicants in a
                // row would otherwise read as two stacked boxes with a doubled
                // edge between them. Stacked bars merge into one stripe instead,
                // while the list's own dividers keep separating the people.
                //
                // The colour needs `!` — `divide-gray-200` on the <ul> sets
                // `border-color` on every child but the first and would repaint
                // the bar grey from the second row on.
                const highlight = bewerber.beworbenAufDiesenBedarf
                  ? 'border-l-[3px] !border-l-primary-600 bg-primary-50/60 pl-3'
                  : bewerber.beworbenAufGleicheSchule
                    ? 'border-l-[3px] !border-l-amber-400 bg-amber-50/60 pl-3'
                    : '';
                // Normally the list already carries the travel time. The button
                // is the fallback for rows the local routing graph could not
                // answer — outside NRW, or while the container was down. Beyond
                // the routing limit the backend refuses, so it is not offered.
                const canCalculateFahrzeit =
                  isRadius &&
                  bewerber.geoPrecision !== 'NONE' &&
                  bewerber.routeDurationMin === null &&
                  bewerber.distanceKm !== null &&
                  bewerber.distanceKm <= MAX_ROUTE_DISTANCE_KM;
                const canInvite =
                  !isNichtGeeignet && bewerber.status === BewerberStatus.GEEIGNET;
                const canPlanKennenlernen =
                  !isNichtGeeignet && bewerber.status === BewerberStatus.IM_POOL;
                const canSendBedarf =
                  !isNichtGeeignet && bedarfSendenStatuses.includes(bewerber.status);
                const stundenWunsch = formatWochenstundenWunsch(
                  bewerber.minWochenstunden,
                  bewerber.maxWochenstunden,
                );

                return (
                  <li
                    key={bewerber.id}
                    className={`py-4 ${highlight} ${isNichtGeeignet ? 'opacity-60' : ''}`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <Link
                          to={`/bewerber/${bewerber.id}`}
                          className="text-sm font-medium text-primary-600 hover:text-primary-500"
                        >
                          {bewerber.firstName} {bewerber.lastName}
                        </Link>
                        <div className="mt-1 flex items-center space-x-2">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${statusConfig?.bgColor || 'bg-gray-100'} ${statusConfig?.color || 'text-gray-800'}`}>
                            {statusConfig?.label || bewerber.status}
                          </span>
                          <span className="text-xs text-gray-500" title={matchConfig.description}>
                            {matchConfig.label}
                          </span>
                          {/* The strongest statement a row can carry, so it
                              leads: an application is a wish, this is a fact. */}
                          {bewerber.zugeordnet && (
                            <span
                              className="inline-flex items-center whitespace-nowrap px-2 py-0.5 rounded text-xs font-medium bg-emerald-100 text-emerald-800"
                              title="Ist dieser Stelle als Schulbegleiter zugeordnet"
                            >
                              Zugeordnet
                            </span>
                          )}
                          {bewerber.beworbenAufDiesenBedarf && (
                            <span
                              className="inline-flex items-center whitespace-nowrap px-2 py-0.5 rounded text-xs font-medium bg-primary-100 text-primary-800"
                              title="Hat sich genau auf diese Stelle beworben"
                            >
                              Direkt beworben
                            </span>
                          )}
                          {/* Assigned elsewhere: a link, because the next thing
                              anyone wants is to see that other Bedarf. */}
                          {!bewerber.zugeordnet && bewerber.zugeordnetAufBedarfId && (
                            <Link
                              to={`/bedarfe/${bewerber.zugeordnetAufBedarfId}`}
                              className="inline-flex items-center whitespace-nowrap px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-700 hover:bg-gray-200"
                              title={`Ist bereits dem Bedarf „${bewerber.zugeordnetAufBedarfName}" zugeordnet`}
                            >
                              Anderweitig zugeordnet
                            </Link>
                          )}
                          {bewerber.beworbenAufGleicheSchule && bewerber.beworbenAufBedarfId && (
                            <Link
                              to={`/bedarfe/${bewerber.beworbenAufBedarfId}`}
                              className="inline-flex items-center whitespace-nowrap px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800 hover:bg-amber-200"
                              title={`Hat sich auf eine andere Stelle derselben Schule beworben${
                                bewerber.beworbenAufBedarfName
                                  ? `: ${bewerber.beworbenAufBedarfName}`
                                  : ''
                              }`}
                            >
                              Gleiche Schule
                            </Link>
                          )}
                          {isNichtGeeignet && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-700">
                              Nicht geeignet
                            </span>
                          )}
                        </div>
                        <div className="mt-1 text-xs text-gray-500">
                          {[
                            bewerber.city && <span>Wohnort: {bewerber.city}</span>,
                            bewerber.ortStellen.length > 0 && (
                              <span>Ort Stelle: {bewerber.ortStellen.join(', ')}</span>
                            ),
                            stundenWunsch && (
                              <span
                                title="Gewünschte Wochenstunden des Bewerbers"
                                className="whitespace-nowrap"
                              >
                                Wunsch: {stundenWunsch}
                              </span>
                            ),
                          ]
                            .filter(Boolean)
                            .map((teil, index) => (
                              <Fragment key={index}>
                                {index > 0 && <span className="mx-1">|</span>}
                                {teil}
                              </Fragment>
                            ))}
                        </div>
                        {isRadius && (
                          <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-gray-600">
                            {/* The driving time leads where it is known — the
                                routing container fills it in for the whole page,
                                so the straight line is only the fallback. */}
                            {bewerber.routeDurationMin !== null ? (
                              <>
                                <span className="font-medium text-gray-900">
                                  {bewerber.routeDurationMin} Min. Fahrt
                                </span>
                                {/* Kept next to the headline it qualifies, ahead
                                    of any badges. */}
                                <span>
                                  {bewerber.routeDistanceKm?.toLocaleString('de-DE')} km ·{' '}
                                  {bewerber.distanceKm?.toLocaleString('de-DE')} km Luftlinie
                                </span>
                              </>
                            ) : (
                              bewerber.distanceKm !== null && (
                                <span className="font-medium">
                                  {bewerber.distanceKm.toLocaleString('de-DE')} km Luftlinie
                                </span>
                              )
                            )}
                            {/* An exactly located address is the normal case;
                                saying so on every row is noise. Only doubt about
                                the distance is worth a chip. */}
                            {bewerber.geoPrecision !== 'EXACT' && (
                              <span
                                className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${precisionConfig.className}`}
                                title={precisionConfig.description}
                              >
                                {precisionConfig.label}
                              </span>
                            )}
                            {bewerber.exceedsMaxPendelKm && (
                              <span
                                className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-700"
                                title="Die Entfernung liegt über der im Vorstellungsgespräch erfassten maximalen Pendelstrecke"
                              >
                                &gt; max. Pendelstrecke ({bewerber.maxPendelKm} km)
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                      {/*
                        Umkreis mode can put five controls in this column. Left
                        unbounded they push the Bewerber details into a narrow
                        column, so the actions wrap onto a second line instead
                        of taking whatever width they like.
                      */}
                      <div className="flex flex-wrap justify-end items-center gap-2 max-w-[20rem]">
                        {canSendBedarf &&
                          (bewerber.bedarfGesendet ? (
                            <span
                              className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-teal-100 text-teal-800"
                              title={
                                bewerber.bedarfGesendetAt
                                  ? `Bedarf gesendet am ${new Date(bewerber.bedarfGesendetAt).toLocaleDateString('de-DE')}`
                                  : 'Bedarf wurde bereits gesendet'
                              }
                            >
                              Bedarf gesendet
                            </span>
                          ) : (
                            <button
                              onClick={() => setSendenBewerber(bewerber)}
                              disabled={!bewerber.email}
                              title={
                                bewerber.email
                                  ? 'Bedarf per E-Mail an diesen Bewerber senden'
                                  : 'Keine E-Mail-Adresse hinterlegt'
                              }
                              className="inline-flex items-center px-3 py-1.5 border border-primary-600 text-xs font-medium rounded-md text-primary-700 bg-white hover:bg-primary-50 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              Bedarf senden
                            </button>
                          ))}
                        {canInvite && (
                          <button
                            onClick={() => handleInvite(bewerber)}
                            disabled={einladen.isInviting}
                            className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-white bg-green-600 hover:bg-green-700 disabled:opacity-50"
                          >
                            Einladen
                          </button>
                        )}
                        {canPlanKennenlernen && (
                          <button
                            onClick={() => handlePlanKennenlernen(bewerber)}
                            disabled
                            title="Bald verfügbar"
                            className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            Kennenlernen
                          </button>
                        )}
                        {isNichtGeeignet ? (
                          <button
                            onClick={() => handleToggleNichtGeeignet(bewerber)}
                            disabled={isTogglingNichtGeeignet}
                            title="Bewerber für diesen Bedarf wieder als geeignet markieren"
                            className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-xs font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                          >
                            Wieder geeignet
                          </button>
                        ) : (
                          <button
                            onClick={() => handleToggleNichtGeeignet(bewerber)}
                            disabled={isTogglingNichtGeeignet}
                            title="Bewerber für diesen Bedarf als nicht geeignet markieren"
                            className="inline-flex items-center px-3 py-1.5 border border-red-200 text-xs font-medium rounded-md text-red-700 bg-white hover:bg-red-50 disabled:opacity-50"
                          >
                            Nicht geeignet
                          </button>
                        )}
                        {canCalculateFahrzeit && (
                          <button
                            onClick={() => handleFahrzeit(bewerber)}
                            disabled={routingId === bewerber.id}
                            title="Diese Strecke konnte nicht automatisch berechnet werden – einmalig nachholen"
                            className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-xs font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {routingId === bewerber.id ? 'Berechne…' : 'Fahrzeit nachholen'}
                          </button>
                        )}
                        <Link
                          to={`/bewerber/${bewerber.id}`}
                          className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-xs font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                        >
                          Details
                        </Link>
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}

          {isRadius && matches.length > 0 && matches.length < total && (
            <div className="mt-4 border-t border-gray-200 pt-4 text-center">
              <button
                type="button"
                onClick={() => setLimit((current) => current + MATCHING_PAGE_SIZE)}
                className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
              >
                Weitere {MATCHING_PAGE_SIZE} anzeigen ({total - matches.length} übrig)
              </button>
            </div>
          )}
        </>,
      )}

      {modalData && (
        <CreateKennenlernenModal
          isOpen={modalData.isOpen}
          onClose={closeModal}
          bedarfId={bedarfId}
          bewerberId={modalData.bewerberId}
          bewerberName={modalData.bewerberName}
          childName={childName}
        />
      )}

      {sendenBewerber && (
        <BedarfSendenModal
          isOpen
          onClose={() => setSendenBewerber(null)}
          bedarfId={bedarfId}
          bewerber={sendenBewerber}
        />
      )}

      <EinladenModal {...einladen.modalProps} />
    </>
  );
}
